import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

import {
  Refresh,
  TrendingUp,
  TrendingDown,
  People,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Assessment,
  Visibility,
  Download
} from '@mui/icons-material';

import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  BarChart,
  PieChart,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  Line,
  Bar,
  Area,
  Cell,
  Pie
} from 'recharts';

import { useAuth } from '../contexts/AuthContext';

interface SystemOverview {
  total_customers: number;
  active_customers: number;
  total_alerts: number;
  resolved_alerts: number;
  total_checkins: number;
  successful_checkins: number;
  system_health_score: number;
  average_response_time: number;
}

interface CustomerMetrics {
  total_customers: number;
  new_customers_this_month: number;
  active_customers: number;
  inactive_customers: number;
  average_health_score: number;
  top_customers: Array<{
    id: number;
    name: string;
    health_score: number;
    last_checkin: string;
  }>;
}

interface AlertAnalytics {
  total_alerts: number;
  alerts_by_type: Array<{
    type: string;
    count: number;
  }>;
  alerts_by_severity: Array<{
    severity: string;
    count: number;
  }>;
  alerts_trend: Array<{
    date: string;
    count: number;
  }>;
}

interface CheckInTrends {
  total_checkins: number;
  successful_checkins: number;
  failed_checkins: number;
  average_response_time: number;
  checkins_by_hour: Array<{
    hour: number;
    count: number;
  }>;
  response_times: Array<{
    date: string;
    average_time: number;
  }>;
}

interface CaregiverAnalytics {
  total_caregivers: number;
  active_caregivers: number;
  primary_caregivers: number;
  pending_invitations: number;
  caregivers_by_role: Array<{
    role: string;
    count: number;
  }>;
}

interface DashboardData {
  system_overview: SystemOverview;
  customer_metrics: CustomerMetrics;
  alert_analytics: AlertAnalytics;
  check_in_trends: CheckInTrends;
  caregiver_analytics: CaregiverAnalytics;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const EnhancedDashboard: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('30');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedCustomer]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const params = new URLSearchParams({
        days: dateRange,
        ...(selectedCustomer !== 'all' && { customer_id: selectedCustomer })
      });

      const [systemOverview, customerMetrics, alertAnalytics, checkInTrends, caregiverAnalytics] = await Promise.all([
        fetch(`/api/v1/reports/system-overview?${params}`, { headers }).then(r => r.json()),
        fetch(`/api/v1/reports/customer-metrics?${params}`, { headers }).then(r => r.json()),
        fetch(`/api/v1/reports/alert-analytics?${params}`, { headers }).then(r => r.json()),
        fetch(`/api/v1/reports/check-in-trends?${params}`, { headers }).then(r => r.json()),
        fetch(`/api/v1/reports/caregiver-analytics?${params}`, { headers }).then(r => r.json())
      ]);

      setData({
        system_overview: systemOverview,
        customer_metrics: customerMetrics,
        alert_analytics: alertAnalytics,
        check_in_trends: checkInTrends,
        caregiver_analytics: caregiverAnalytics
      });

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getResponseRateColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  const formatResponseTime = (minutes: number) => {
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Failed to load dashboard data'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Enhanced Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Comprehensive analytics and insights for your wellness system
            </Typography>
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {/* System Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Customers
                  </Typography>
                  <Typography variant="h4">
                    {data.system_overview.total_customers}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Alerts
                  </Typography>
                  <Typography variant="h4">
                    {data.system_overview.total_alerts - data.system_overview.resolved_alerts}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    System Health
                  </Typography>
                  <Typography variant="h4">
                    {data.system_overview.system_health_score}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: getHealthScoreColor(data.system_overview.system_health_score) + '.main' }}>
                  <Assessment />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Response Time
                  </Typography>
                  <Typography variant="h4">
                    {formatResponseTime(data.system_overview.average_response_time)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <Schedule />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Alert Trends */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Alert Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.alert_analytics.alerts_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Alert Types Distribution */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Alert Types
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                                     <Pie
                     data={data.alert_analytics.alerts_by_type}
                     cx="50%"
                     cy="50%"
                     labelLine={false}
                     label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                     outerRadius={80}
                     fill="#8884d8"
                     dataKey="count"
                   >
                    {data.alert_analytics.alerts_by_type.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Check-in Response Times */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Response Time Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.check_in_trends.response_times}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="average_time" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Check-ins by Hour */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Check-ins by Hour
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.check_in_trends.checkins_by_hour}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Customers Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Customers by Health Score
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Health Score</TableCell>
                  <TableCell>Last Check-in</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.customer_metrics.top_customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {customer.name.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">
                          {customer.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${customer.health_score}%`}
                        color={getHealthScoreColor(customer.health_score)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(customer.last_checkin).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        icon={<CheckCircle />}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Caregiver Analytics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Caregiver Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.caregiver_analytics.caregivers_by_role}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="role" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Caregiver Summary
              </Typography>
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {data.caregiver_analytics.total_caregivers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Caregivers
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {data.caregiver_analytics.active_caregivers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active Caregivers
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {data.caregiver_analytics.primary_caregivers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Primary Caregivers
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {data.caregiver_analytics.pending_invitations}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending Invitations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EnhancedDashboard;
